import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { countBy, keyBy } from 'lodash-es';
import { Box, Button, PageEmptyState, Spinner, TableList } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useFetchAgreementStudioData } from '~/components/AgreementStudioPage/hooks/useFetchAgreementStudioData';
import GenericError from '~/components/GenericError/GenericError';
import { useStoreActions } from '~/contexts';
import { useClients } from '~/hooks/reactQuery/queries/useClients';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useOnMount } from '~/hooks/useOnMount';
import { useRestfulWrapper } from '~/hooks/useRestfulWrapper';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import { useStore } from '~/store/store-context';
import { EmptyStateIllustration } from '../EmptyStateIllustration';
import { editDraftsTableHeaderColumnDefs, getUIClientName } from './EditDraftsTable.utils';
import { EditDraftsTableRow } from './EditDraftsTableRow';
import { useEditDraftsTableRowActions } from './hooks/useEditDraftsTableRowActions';
import { useVisibleSortedDrafts } from './hooks/useVisibleSortedDrafts';
import { ColumnSortId, EditDraftsTableColumns } from './types';
const EDIT_DRAFTS_TABLE_PAGE_SIZE = 30;
export const GetEditDraftsTableTID = (parentTestId)=>getTestIds(parentTestId);
export function EditDraftsTableWithData(props) {
    const { visibleColumns, search, selected, setSelected, visibleDraftIds, changedDraftIds, onHighlightEnd, onClearSearch } = props;
    const { EditDraftsTableTID } = useMemo(()=>GetEditDraftsTableTID(), []);
    const { t: draftsTexts } = useLocaleKeys('proposal.draftsDashboard');
    const columnsTexts = draftsTexts.columns;
    const [drafts, setDrafts] = useState(props.drafts);
    useEffect(()=>setDrafts(props.drafts), [
        props.drafts
    ]);
    const [scrollContainerEl, setScrollContainerEl] = useState(null);
    const [sort, setSort] = useState({
        sortId: ColumnSortId.LastModified,
        sort: 'desc'
    });
    const { contacts } = useContacts();
    const headerItems = useMemo(()=>editDraftsTableHeaderColumnDefs(visibleColumns, columnsTexts), [
        visibleColumns,
        columnsTexts
    ]);
    const contactMap = useMemo(()=>keyBy(contacts || [], (c)=>c.id()), [
        contacts
    ]);
    const { clients } = useClients({
        enabled: visibleColumns[EditDraftsTableColumns.Client]
    });
    const clientMap = useMemo(()=>keyBy(clients, (client)=>client.clientId()), [
        clients
    ]);
    const getContact = useCallback((draft)=>{
        const clientContactId = draft.clientContactId();
        return clientContactId ? contactMap[clientContactId] || null : null;
    }, [
        contactMap
    ]);
    const anchorSampleLabel = draftsTexts.cells.shared.anchorSample();
    const unnamedDraftLabel = draftsTexts.cells.shared.unnamedDraft();
    const getClientName = useCallback((draft)=>{
        var _clientMap_customerId;
        const customerId = draft.customerId();
        const client = customerId != null ? (_clientMap_customerId = clientMap[customerId]) !== null && _clientMap_customerId !== void 0 ? _clientMap_customerId : null : null;
        return getUIClientName(draft, client, getContact(draft), anchorSampleLabel);
    }, [
        clientMap,
        getContact,
        anchorSampleLabel
    ]);
    const sortedAndFilteredDrafts = useVisibleSortedDrafts({
        drafts,
        getContact,
        getClientName,
        search,
        visibleDraftIds,
        sort,
        anchorSampleLabel,
        unnamedDraftLabel
    });
    const resetKey = useMemo(()=>({
            sort,
            search,
            visibleDraftIds
        }), [
        sort,
        search,
        visibleDraftIds
    ]);
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: sortedAndFilteredDrafts,
        pageSize: EDIT_DRAFTS_TABLE_PAGE_SIZE,
        scrollElRef: scrollContainerEl,
        resetKey
    });
    const worksAt = useStore(selectors.user.business);
    const { rowActions } = useEditDraftsTableRowActions({
        setDrafts
    });
    const onToggleAllSelection = useCallback((selectAll)=>setSelected(drafts.reduce((acc, d)=>Object.assign(acc, {
                [d.id()]: selectAll
            }), {})), [
        drafts,
        setSelected
    ]);
    const numSelected = useMemo(()=>{
        var _countBy_true;
        return (_countBy_true = countBy(selected).true) !== null && _countBy_true !== void 0 ? _countBy_true : 0;
    }, [
        selected
    ]);
    const rowProps = (draft, index)=>({
            draft,
            index,
            visibleColumns,
            getContact,
            getClientName,
            actions: rowActions,
            worksAt,
            highlighted: changedDraftIds.has(draft.id()),
            onHighlightEnd
        });
    const onSortChange = (next)=>{
        if (next == null) return setSort({
            sortId: headerItems[0].sortId,
            sort: 'asc'
        });
        setSort({
            sortId: headerItems[next.colIndex].sortId,
            sort: next.sort
        });
    };
    const tableListSort = useMemo(()=>({
            sort: sort.sort,
            colIndex: headerItems.findIndex((item)=>item.sortId === sort.sortId)
        }), [
        sort,
        headerItems
    ]);
    return /*#__PURE__*/ _jsx(Box, {
        testId: EditDraftsTableTID.Root(),
        children: /*#__PURE__*/ _jsx(TableList.Provider, {
            hasActionColumn: true,
            columnDefs: headerItems,
            expandableRows: true,
            expandableRowsVariant: "clickableIcon",
            enableRowSelection: true,
            selectedRows: selected,
            onSelectionChange: setSelected,
            numRows: drafts.length,
            numSelectedRows: numSelected,
            onToggleAllSelection: onToggleAllSelection,
            sort: tableListSort,
            onSortChange: onSortChange,
            children: /*#__PURE__*/ _jsx(TableList.Structure, {
                containerRef: setScrollContainerEl,
                testId: EditDraftsTableTID.ScrollContainer(),
                onScroll: onScroll,
                header: /*#__PURE__*/ _jsx(TableList.Header, {
                    stickyToTop: true,
                    items: headerItems,
                    testId: EditDraftsTableTID.Header()
                }),
                content: sortedAndFilteredDrafts.length === 0 ? /*#__PURE__*/ _jsx(PageEmptyState, {
                    testId: EditDraftsTableTID.EmptyState(),
                    illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                    title: draftsTexts.noDraftsMatchingFilters.title(),
                    message: draftsTexts.noDraftsMatchingFilters.message(),
                    actions: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'clear_drafts_search'
                        },
                        testId: EditDraftsTableTID.EmptyStateClearButton(),
                        onClick: onClearSearch,
                        children: draftsTexts.clearSearch()
                    })
                }) : /*#__PURE__*/ _jsx(_Fragment, {
                    children: paginatedData.map((draft, index)=>/*#__PURE__*/ _jsx(EditDraftsTableRow, _object_spread({}, rowProps(draft, index)), draft.id()))
                })
            })
        })
    });
}
export const EditDraftsTable = /*#__PURE__*/ forwardRef((param, ref)=>{
    let { visibleColumns, search, selected, setSelected, visibleDraftIds, changedDraftIds, onHighlightEnd, onClearSearch } = param;
    const { api } = useStoreActions();
    const { EditDraftsTableTID } = useMemo(()=>GetEditDraftsTableTID(), []);
    const fetchDrafts = ()=>api.draft.getFullDrafts({
            limit: 1000,
            page: 1
        });
    const { refresh, renderRestfulState } = useRestfulWrapper({
        fetch: fetchDrafts
    });
    const fetchAgreementStudioData = useFetchAgreementStudioData({
        disableLoader: true,
        disableAgreements: true,
        disableProposals: true
    });
    useOnMount(()=>{
        fetchAgreementStudioData();
    });
    useImperativeHandle(ref, ()=>({
            refresh
        }));
    const withDataProps = (drafts)=>({
            drafts,
            visibleColumns,
            search,
            selected,
            setSelected,
            visibleDraftIds,
            changedDraftIds,
            onHighlightEnd,
            onClearSearch
        });
    return renderRestfulState({
        renderLoader: ()=>/*#__PURE__*/ _jsx(Spinner, {
                centeredInBox: true,
                centerBoxHeight: "100%",
                testId: EditDraftsTableTID.Spinner()
            }),
        renderError: ()=>/*#__PURE__*/ _jsx(GenericError, {
                testId: EditDraftsTableTID.Error()
            }),
        renderChild: (param)=>{
            let { data } = param;
            return /*#__PURE__*/ _jsx(EditDraftsTableWithData, _object_spread({}, withDataProps(data)));
        }
    });
});
